import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, {useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useGateAccess} from '../hooks/useGateAccess';
import ComponentControls from './ComponentControls';
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import TimestampBadge from "./TimestampBadge";
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { buildPanelClassName } from '../utils/panelTheme';
import { extractFirstYouTubeUrlFromText, getYouTubeEmbedUrl } from '../utils/youtubeEmbed';

export default function TextComponent({
                                          comp,
                                          index,
                                          moveUp,
                                          moveDown,
                                          onEdit,
                                          isEditor = false,
                                          removeComponent,

                                          // gating props
                                          onGateTypeChange,
                                          onGateListChange,
                                          extraGates,
                                          gateLogic,
                                          onExtraGatesChange,
                                          onGateLogicChange,

                                          donationCampaignOptions = [],
                                          donationGate,
                                          onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,

                                          style = {},
                                          onStyleChange,
                                          coverUrl = '',
                                          onCoverChange,
                                          onPanelBackgroundImageChange,
                                          timestampDisplay = 'none',
                                          onTimestampDisplayChange,
                                          pageTypography,

                                          // NEW
                                          displayMode,                  // 'full' | 'compressed' (editor wiring)
                                          onDisplayModeChange,
                                          ownerPublicKey = '',
                                      }) {
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate = normalizeGate(comp.gate || {type: 'public', listId: ''});
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );
    const logic = comp.gateLogic === 'all' ? 'all' : 'any';
    const [gateRefreshKey, setGateRefreshKey] = useState(0);
    const donationMap = useMemo(() => {
        const m = new Map();
        // expect options like { id, campaignName, currency }
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const [hasAccess, isLoading, gateNamesLabel, gateNames, gateAccess] =
        useGateAccess({ gates, logic, donationLookup: donationMap }, { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id });
    const { resetPasswordAccess, resetPasswordError } = gateAccess;

    // username for deep link
    const {username} = useParams();
    const mode = (displayMode || comp.displayMode || 'full');
    const donationGateLinks = useMemo(() => {
        const safeUsername = username || comp?.muultiName || '';
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, username, comp?.muultiName]);
    const detailHref =
        username && comp?.id
            ? `/${username}/c/${comp.id}`
            : null;

    // global + panel typography resolved (panel overrides win)
    const typo = resolveTypography(pageTypography, style);

    // normalize sections
    const sections = Array.isArray(comp.sections) && comp.sections.length
        ? comp.sections
        : [{
            heading: comp.heading || '',
            subheading: comp.subheading || '',
            text: comp.text || '',
            featureImageUrl: comp.featureImageUrl || ''
        }];

    const first = sections[0] || {};
    const firstLinkEmbedUrl = getYouTubeEmbedUrl(first.linkUrl || '');
    const firstTextEmbedUrl = getYouTubeEmbedUrl(extractFirstYouTubeUrlFromText(first.text || ''));
    const first10Lines = (text = '') => {
        const lines = String(text).split('\n');
        const slice = lines.slice(0, 10).join('\n');
        return lines.length > 10 ? `${slice}\n…` : slice;
    };

    const lh = v => (typeof v === 'number' ? v : (v || undefined));

    // small helper to accept numbers or CSS strings
    const numToPx = v => (typeof v === 'number' ? `${v}px` : (v || undefined));

    // editor-only toggle
    const EditorDisplayToggle = () => {
        if (typeof onDisplayModeChange !== 'function' || typeof index !== 'number') return null;
        return (
            <div>
                <label>Display</label>
                <select
                    value={mode}
                    onChange={e => onDisplayModeChange(index, e.target.value)}
                >
                    <option value="full">Full</option>
                    <option value="compressed">Compressed</option>
                </select>
            </div>
        );
    };

    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );

    // loading state (unchanged)
    if (isLoading) {
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                <h3>Checking access for {gateNamesLabel}…</h3>
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style} onStyleChange={onStyleChange}
                    coverUrl={coverUrl} onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    // locked view (unchanged)
    if (!hasAccess) {
        const directPaymentGate =
            (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate =
            (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: "locked" })} style={style}>
                <h1>{deniedHeading} - {gateNamesLabel}</h1>
                {passwordGate ? (
                    <PasswordUnlock
                        gate={passwordGate}
                        componentId={comp.id}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {directPaymentGate ? (
                    <DirectPaymentUnlock
                        gate={directPaymentGate}
                        componentId={comp.id}
                        componentType={comp.type}
                        ownerPublicKey={ownerPublicKeyStr}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {donationGateLinks.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {donationGateLinks.map((d) => (
                            <div key={d.id} style={{ marginTop: 4 }}>
                                <Link to={d.href} className="detail-page-return-link">
                                    View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                </Link>
                            </div>
                        ))}
                    </div>
                )}

                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index} moveUp={moveUp} moveDown={moveDown} onEdit={onEdit} removeComponent={removeComponent}
                    gateType={gate.type} selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange} onGateListChange={onGateListChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style} onStyleChange={onStyleChange}
                    coverUrl={coverUrl} onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                />
            </div>
        </GateRevealPanel>
        );
    }

    // compressed
    if (mode === 'compressed') {
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                {/* image if present */}
                {first.featureImageUrl && (
                    <div className='text-component-image-container'>
                        <img className='text-component-image' src={first.featureImageUrl} alt=""/>
                    </div>
                )}

                {/* headings */}
                {first.heading && (
                    <h1
                        style={{
                            fontFamily: typo.headers.fontFamily,
                            fontSize: numToPx(typo.headers.fontSize),
                            fontWeight: typo.headers.fontWeight,
                            color: typo.headers.color,
                            lineHeight: typo.headers?.lineHeight
                        }}
                    >
                        {first.heading}
                    </h1>
                )}
                {first.subheading && (
                    <h2
                        style={{
                            marginTop: 4,
                            fontFamily: typo.headers.fontFamily,
                            color: typo.headers.color || '#555',
                            lineHeight: typo.headers?.lineHeight
                        }}
                    >
                        {first.subheading}
                    </h2>
                )}

                {/* text (first 10 lines) */}
                {first.text && (
                    <>
                        <p
                            style={{
                                marginTop: 8,
                                whiteSpace: 'pre-wrap',
                                fontFamily: typo.paragraph.fontFamily,
                                fontSize: numToPx(typo.paragraph.fontSize),
                                color: typo.paragraph.color,
                                lineHeight: typo.headers?.lineHeight
                            }}
                        >
                            {first10Lines(first.text)}
                        </p>
                        {firstTextEmbedUrl && (
                            <div className="component-youtube-embed">
                                <div className="component-youtube-embed-frame">
                                    <iframe
                                        src={firstTextEmbedUrl}
                                        title="YouTube video"
                                        loading="lazy"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* NEW: if first section is a video, show lightweight preview (title/description + player) */}
                {first.type === 'video' && (
                    <div style={{marginTop: 8}}>
                        {first.title && (
                            <h1
                                style={{
                                    fontFamily: typo.headers.fontFamily,
                                    fontSize: numToPx(typo.headers.fontSize),
                                    fontWeight: typo.headers.fontWeight,
                                    color: typo.headers.color,
                                    lineHeight: typo.headers?.lineHeight
                                }}
                            >
                                {first.title}
                            </h1>
                        )}
                        {first.description && (
                            <p
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: typo.paragraph.fontFamily,
                                    fontSize: numToPx(typo.paragraph.fontSize),
                                    color: typo.paragraph.color,
                                    lineHeight: lh(typo.paragraph.lineHeight)
                                }}
                            >
                                {first10Lines(first.description)}
                            </p>
                        )}
                        {first.videoUrl && (
                            <video
                                className='text-component-video'
                                src={first.videoUrl}
                                controls
                            />
                        )}
                    </div>
                )}

                {first.type === 'link' && (
                    <div style={{marginTop: 8}}>
                        {first.title && (
                            <h1
                                style={{
                                    fontFamily: typo.headers.fontFamily,
                                    fontSize: numToPx(typo.headers.fontSize),
                                    fontWeight: typo.headers.fontWeight,
                                    color: typo.headers.color,
                                    lineHeight: lh(typo.headers.lineHeight)
                                }}
                            >
                                {first.title}
                            </h1>
                        )}
                        {first.description && (
                            <p
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: typo.paragraph.fontFamily,
                                    fontSize: numToPx(typo.paragraph.fontSize),
                                    color: typo.paragraph.color,
                                    lineHeight: typo.paragraph.lineHeight
                                }}
                            >
                                {first10Lines(first.description)}
                            </p>
                        )}
                        {first.videoUrl && (
                            <video
                                className='text-component-video'
                                src={first.videoUrl}
                                controls
                            />
                        )}
                    </div>
                )}

                {first.linkUrl && (
                    <div style={{ marginTop: 8 }}>
                        {firstLinkEmbedUrl ? (
                            <div className="component-youtube-embed">
                                <div className="component-youtube-embed-frame">
                                    <iframe
                                        src={firstLinkEmbedUrl}
                                        title={first.linkTitle || 'YouTube video'}
                                        loading="lazy"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        ) : (
                            <p style={{ marginTop: 8 }}>
                                <a
                                    href={first.linkUrl}
                                    className="textcomponent-link"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        color: typo.links.color,
                                        textDecoration: typo.links.textDecoration || undefined
                                    }}
                                >
                                    {first.linkTitle || first.linkUrl}
                                </a>
                            </p>
                        )}
                    </div>
                )}

                {detailHref && (
                    <div style={{ marginTop: 8 }}>
                        <Link
                            to={detailHref}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                fontFamily: typo.links.fontFamily,
                                fontSize: typeof typo.links.fontSize === 'number' ? `${typo.links.fontSize}px` : typo.links.fontSize,
                                fontWeight: typo.links.fontWeight,
                                color: typo.links.color,
                                textDecoration: typo.links.textDecoration || 'underline',
                                lineHeight: typo.links.lineHeight || 'normal',
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer'
                            }}
                        >
                            Open in new page →
                        </Link>
                    </div>
                )}


                {hasAccess && <GateBadge gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />}
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                <EditorDisplayToggle/>
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                />
            </div>
        </GateRevealPanel>
        );
    }

    // full
    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
            {sections.map((s, i) => {
                const sectionLinkEmbedUrl = getYouTubeEmbedUrl(s.linkUrl || '');
                const sectionTextEmbedUrl = getYouTubeEmbedUrl(extractFirstYouTubeUrlFromText(s.text || ''));
                return (
                <section key={i} style={{marginBottom: 24}}>
                    {/* image */}
                    {s.heading && (
                        <h1
                            style={{
                                fontFamily: typo.headers.fontFamily,
                                fontSize: numToPx(typo.headers.fontSize),
                                fontWeight: typo.headers.fontWeight,
                                color: typo.headers.color,
                                lineHeight: lh(typo.headers.lineHeight)
                            }}
                        >
                            {s.heading}
                        </h1>
                    )}
                    {s.featureImageUrl && (
                        <div className='text-component-image-container'>
                            <img className='text-component-image' src={s.featureImageUrl} alt=""/>
                        </div>
                    )}

                    {/* headings */}
                    {s.subheading && (
                        <h2
                            style={{
                                marginTop: 4,
                                fontFamily: typo.headers.fontFamily,
                                color: typo.headers.color || '#555',
                                lineHeight: lh(typo.headers.lineHeight)
                            }}
                        >
                            {s.subheading}
                        </h2>
                    )}

                    {/* NEW: video section */}
                    {s.type === 'video' && s.videoUrl ? (
                        <div style={{marginTop: 8}}>
                            {s.title && (
                                <h1
                                    style={{
                                        fontFamily: typo.headers.fontFamily,
                                        fontSize: numToPx(typo.headers.fontSize),
                                        fontWeight: typo.headers.fontWeight,
                                        color: typo.headers.color,
                                        lineHeight: typo.headers.lineHeight
                                    }}
                                >
                                    {s.title}
                                </h1>
                            )}
                            {s.description && (
                                <p
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: typo.paragraph.fontFamily,
                                        fontSize: numToPx(typo.paragraph.fontSize),
                                        color: typo.paragraph.color,
                                        lineHeight: typo.paragraph.lineHeight
                                    }}
                                >
                                    {s.description}
                                </p>
                            )}
                            <video
                                className='text-component-video'
                                src={s.videoUrl}
                                controls
                            />
                        </div>
                    ) : Array.isArray(s.items) && s.items.length > 0 ? (
                        <ul style={{paddingLeft: 18, marginTop: 8}}>
                            {s.items.map((it, idx) => <li key={idx}>{it}</li>)}
                        </ul>
                    ) : s.linkUrl ? (
                        sectionLinkEmbedUrl ? (
                            <div className="component-youtube-embed">
                                <div className="component-youtube-embed-frame">
                                    <iframe
                                        src={sectionLinkEmbedUrl}
                                        title={s.linkTitle || 'YouTube video'}
                                        loading="lazy"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        ) : (
                            <p style={{marginTop: 8}}>
                                <a
                                    href={s.linkUrl}
                                    className="textcomponent-link"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        color: typo.links.color,
                                        textDecoration: typo.links.textDecoration || undefined
                                    }}
                                >
                                    {s.linkTitle || s.linkUrl}
                                </a>
                            </p>
                        )
                    ) : s.text ? (
                        <>
                            <p
                                style={{
                                    marginTop: 8,
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: typo.paragraph.fontFamily,
                                    fontSize: numToPx(typo.paragraph.fontSize),
                                    color: typo.paragraph.color,
                                    lineHeight: lh(typo.paragraph.lineHeight)
                                }}
                            >
                                {s.text}
                            </p>
                            {sectionTextEmbedUrl && (
                                <div className="component-youtube-embed">
                                    <div className="component-youtube-embed-frame">
                                        <iframe
                                            src={sectionTextEmbedUrl}
                                            title="YouTube video"
                                            loading="lazy"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    ) : null}
                </section>
                );
            })}

            {detailHref && (
                <div style={{ marginTop: 8 }}>
                    <Link
                        to={detailHref}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            fontFamily: typo.links.fontFamily,
                            fontSize: typeof typo.links.fontSize === 'number' ? `${typo.links.fontSize}px` : typo.links.fontSize,
                            fontWeight: typo.links.fontWeight,
                            color: typo.links.color,
                            textDecoration: typo.links.textDecoration || 'underline',
                            lineHeight: typo.links.lineHeight || 'normal',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer'
                        }}
                    >
                        Open in new page →
                    </Link>
                </div>
            )}

            {hasAccess && (
                <GateBadge  gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            <EditorDisplayToggle/>
            {analyticsFooter}
            <ComponentControls
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay || 'none'}
                onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
            />
        </div>
        </GateRevealPanel>
    );
}



